import Chart from "chart.js";
import "chartjs-plugin-annotation";
//import "chartjs-plugin-zoom"; // enables pan/zoom on Chart.js v2
import PubSub from "pubsub-js";

import log from "../services/log";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import common from "../services/common";
import Darkmode from "../services/darkmode";

let _zoomPluginLoaded = false;

async function ensureZoomPluginLoaded() {
  if (_zoomPluginLoaded) return;
  await import("chartjs-plugin-zoom");
  _zoomPluginLoaded = true;
}

dayjs.extend(utc);

let column = "rsi";
let prev_column = "prev_rsi";
let RSI_INTERVAL = "4h";
let coins_count = 150;

/**
 * Plugin: draw coin labels + move lines + RSI zone labels
 */
const RSIOverlayPlugin = {
  afterDatasetsDraw: function (chart) {
    const opts = chart.options || {};
    const p = opts._cw || {};
    const theme = p.theme || {};
    const darkmode = !!p.darkmode;

    const ctx = chart.ctx;
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);

    const xScale = chart.scales["x-axis-1"];
    const yScale = chart.scales["y-axis-1"];

    // Move lines (prev -> current)
    if (p.drawMoves && Array.isArray(p.moves)) {
      ctx.save();
      ctx.lineWidth = 1.5;
      ctx.setLineDash([2, 4]);

      for (let i = 0; i < p.moves.length; i++) {
        const m = p.moves[i];
        const x = xScale.getPixelForValue(m.x);
        const y0 = yScale.getPixelForValue(m.y0);
        const y1 = yScale.getPixelForValue(m.y1);

        ctx.beginPath();
        ctx.moveTo(x, y0);
        ctx.lineTo(x, y1);
        ctx.strokeStyle = m.color;
        ctx.stroke();
      }
      ctx.restore();
    }

    // Coin symbol labels above points
    if (p.drawLabels) {
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.font = "12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
      ctx.fillStyle = theme.fontColor || (darkmode ? "#fff" : "#6c6c6c");

      const data = dataset.data || [];
      for (let i = 0; i < meta.data.length; i++) {
        const el = meta.data[i];
        const d = data[i];
        if (!d || !d._coin || !d._coin.coin) continue;
        ctx.fillText(d._coin.coin, el._model.x, el._model.y - 6);
      }
      ctx.restore();
    }

    // RSI zone labels
    if (Array.isArray(p.rsiLabels) && p.rsiLabels.length) {
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "18px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
      ctx.fillStyle = theme.rsiLabelsFontColor || (darkmode ? "#ddd" : "#444");

      for (let i = 0; i < p.rsiLabels.length; i++) {
        const t = p.rsiLabels[i];
        const x = xScale.getPixelForValue(t.x);
        const y = yScale.getPixelForValue(t.y);
        ctx.fillText(t.text, x, y);
      }
      ctx.restore();
    }

    // Custom title lines with per-line font sizes
    if (Array.isArray(p.titleLines) && p.titleLines.length) {
      const paddingTop = chart.chartArea ? chart.chartArea.top : 0;
      const paddingBottom = 20;
      const gap = 10;
      const line1 = p.titleLines[0];
      const line2 = p.titleLines[1];
      if (line1 && line2 && paddingTop > 0) {
        const totalHeight = line1.size + gap + line2.size + paddingBottom;
        const topY = Math.max(0, (paddingTop - totalHeight) / 2);
        const y1 = topY + line1.size / 2;
        const y2 = y1 + line1.size / 2 + gap + line2.size / 2;
        const x = (chart.chartArea.left + chart.chartArea.right) / 2;

        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = p.titleColor || theme.fontColor || (darkmode ? "#fff" : "#333");

        ctx.font = `${line1.size}px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial`;
        ctx.fillText(line1.text, x, y1);

        ctx.font = `${line2.size}px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial`;
        ctx.fillText(line2.text, x, y2);
        ctx.restore();
      }
    }
  },
};

Chart.plugins.register(RSIOverlayPlugin);

const Charts = {
  rootEl: null, // div or canvas you pass in
  canvasEl: null,
  ctx2d: null,
  chart: null,
  firstDraw: 0,
  saveBtn: null,

  init: (id) => {
    const el = document.getElementById(id);
    if (!el) throw new Error(`Charts.init: element #${id} not found`);
    Charts.rootEl = el;

    // Ensure container can position overlay button
    if (Charts.rootEl.style.position !== "relative") {
      Charts.rootEl.style.position = "relative";
    }

    // If it's already a canvas, use it, else inject a canvas
    if (el.tagName && el.tagName.toLowerCase() === "canvas") {
      Charts.canvasEl = el;
    } else {
      let canvas = el.querySelector("canvas[data-cw-chart='rsi']");
      if (!canvas) {
        canvas = document.createElement("canvas");
        canvas.setAttribute("data-cw-chart", "rsi");
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        el.innerHTML = "";
        el.appendChild(canvas);
      }
      Charts.canvasEl = canvas;
    }

    // --- Mobile scroll fix: allow vertical swipes to scroll the page ---
    // Chart.js v2 / zoom plugin may attach non-passive touch listeners and prevent scrolling.
    // We add passive listeners and stop Chart.js from hijacking vertical swipes.
    // Tap still works (tooltip + select).
    if (!Charts._touchGuardAttached) {
      Charts._touchGuardAttached = true;

      let startX = 0;
      let startY = 0;
      let isVertical = false;

      const onTouchStart = (e) => {
        if (!e.touches || e.touches.length !== 1) return;
        const t = e.touches[0];
        startX = t.clientX;
        startY = t.clientY;
        isVertical = false;
      };

      const onTouchMove = (e) => {
        if (!e.touches || e.touches.length !== 1) return;
        const t = e.touches[0];
        const dx = Math.abs(t.clientX - startX);
        const dy = Math.abs(t.clientY - startY);

        // If the gesture is mostly vertical, let the browser scroll the page.
        if (dy > dx && dy > 6) {
          isVertical = true;
          // Stop Chart.js/zoom-plugin handlers from seeing this move (they often call preventDefault)
          e.stopImmediatePropagation?.();
          e.stopPropagation();
          // Do NOT preventDefault -> allow scroll
        }
      };

      const onTouchEnd = (e) => {
        // reset
        startX = 0;
        startY = 0;
        isVertical = false;
      };

      // Capture phase + passive so we don’t block scrolling
      Charts.canvasEl.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
      Charts.canvasEl.addEventListener("touchmove", onTouchMove, { passive: true, capture: true });
      Charts.canvasEl.addEventListener("touchend", onTouchEnd, { passive: true, capture: true });
    }

    // Allow page scrolling on mobile when finger is over the canvas
    Charts.canvasEl.style.touchAction = "pan-y";
    Charts.canvasEl.style.webkitUserSelect = "none";
    Charts.canvasEl.style.userSelect = "none";

    Charts.ctx2d = Charts.canvasEl.getContext("2d");

    // Add "Save image" button (mini modebar)
    /*if (!Charts.saveBtn) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = "Save Heatmap";
      btn.style.position = "absolute";
      btn.style.top = "8px";
      btn.style.right = "8px";
      btn.style.zIndex = "10";
      btn.style.padding = "6px 10px";
      btn.style.borderRadius = "8px";
      btn.style.border = "1px solid rgba(255,255,255,0.2)";
      btn.style.background = "rgba(0,0,0,0.35)";
      btn.style.color = "#fff";
      btn.style.cursor = "pointer";
      btn.style.fontSize = "12px";
      btn.style.backdropFilter = "blur(6px)";
      btn.onmouseenter = () => (btn.style.background = "rgba(0,0,0,0.55)");
      btn.onmouseleave = () => (btn.style.background = "rgba(0,0,0,0.35)");
      btn.onclick = () => {
        Charts.saveImage();
      };

      Charts.rootEl.appendChild(btn);
      Charts.saveBtn = btn;
    }*/
  },

  refreshSize: () => {
    if (!Charts.chart || !Charts.canvasEl) return;

    // wait until the container actually has size (tab is visible)
    const tryResize = (tries = 0) => {
      const w = Charts.canvasEl.parentElement ? Charts.canvasEl.parentElement.clientWidth : 0;
      const h = Charts.canvasEl.parentElement ? Charts.canvasEl.parentElement.clientHeight : 0;

      if (w > 0 && h > 0) {
        Charts.chart.resize();
        Charts.chart.update(0);
        return;
      }

      // try a few frames (tab switch/layout can take a tick)
      if (tries < 20) requestAnimationFrame(() => tryResize(tries + 1));
    };

    requestAnimationFrame(() => tryResize(0));
  },

  destroy: () => {
    if (Charts.chart) {
      Charts.chart.destroy();
      Charts.chart = null;
      Charts.firstDraw = 0;
    }
    if (Charts.saveBtn && Charts.saveBtn.parentNode) {
      Charts.saveBtn.parentNode.removeChild(Charts.saveBtn);
    }
    Charts.saveBtn = null;
  },

  saveImage: (filename) => {
    if (!Charts.canvasEl) return;
    const name = filename || `cryptowavesapp_heatmap_${dayjs.utc().format("DD_MM_YYYY_HH_mm")}.png`;

    const link = document.createElement("a");
    link.download = name;
    link.href = Charts.canvasEl.toDataURL("image/png");
    link.click();
  },

  rsi: async (data, portfolio, filters, alerts, isMobile = false, options, indicators, darkmode = false) => {
    const report_time = dayjs.utc().format("DD/MM/YYYY hh:mm A");
    const report_time_download = dayjs.utc().format("DD_MM_YYYY_hh_mm_A");
    log.traceTag("Charts.rsi(Chart.js v2)", "refresh");

    // Only load pan/zoom plugin on desktop.
    // This prevents it from hijacking touch "swipe-to-scroll" in mobile mode.
    if (!isMobile) {
      await ensureZoomPluginLoaded();
    }

    column = options.rsi_1d ? "rsi_1d" : "rsi";
    prev_column = options.rsi_1d ? "prev_rsi_1d" : "prev_rsi";
    RSI_INTERVAL = options.rsi_1d ? "1D" : "4h";

    const theme = Darkmode.get(darkmode).chart;
    const gridLineColor = darkmode ? "rgba(255,255,255,0.08)" : "rgba(29,34,56,0.08)";
    const pointBorderColor = darkmode ? "rgba(255,255,255,0.18)" : "rgba(29,34,56,0.22)";

    const buyCoins = data.filter((x) => x.rsi_1d && x[column] <= 40);
    const sellCoins = data.filter((x) => x.rsi_1d && x[column] >= 60);
    const waitCoins = data.filter((x) => x.rsi_1d && x[column] > 40 && x[column] < 60);

    let filtered = [];
    if (filters.buy) filtered = filtered.concat(buyCoins);
    if (filters.sell) filtered = filtered.concat(sellCoins);
    if (filters.wait) filtered = filtered.concat(waitCoins);

    if (!filters.buy && !filters.sell && !filters.wait) {
      filtered = buyCoins.concat(sellCoins).concat(waitCoins);
    }

    const search = filters.search === "" ? "$$$" : filters.search;

    const points = filtered.map((coin) => {
      const alertObj = alerts.find((a) => a.coin === coin.coin);
      const isSearch = coin.coin.includes(search);

      let color = darkmode ? "rgb(93, 93, 93)" : "rgb(143, 143, 143)";
      if (isSearch) color = "orange";
      else if (!filters.portfolio && options["portfolio"] && portfolio.includes(coin.coin))
        color = "rgba(175, 0, 255, 1)";
      else if (options["alerts"] && alertObj)
        color = common.getHeatmapColorByAction(alertObj.signal).color;

      // bubble sizes: unchanged
      let radius = 5;                // was 10
      if (isSearch) radius = 7;     // was 16
      else radius = portfolio.includes(coin.coin) ? 7 : (alertObj ? 5 : 5); // was 12/10

      const hit = Math.max(10, radius + 6); // easy hover without visual growth

    return {
        x: coin.rank,
        y: coin[column],
        _coin: coin,
        _prev: coin[prev_column],
        _color: color,
        _radius: radius,
        _hitRadius: hit,
      };
    });

    const rsiValues = points.map((p) => p.y).filter((v) => Number.isFinite(v));
    const rsiMin = rsiValues.length ? Math.min(...rsiValues) : 20;
    const rsiMax = rsiValues.length ? Math.max(...rsiValues) : 80;
    const yMin = Math.min(rsiMin, 20);
    const yMax = Math.max(rsiMax, 80);

    // move segments for plugin
    const moves = [];
    if (!isMobile && options["moves"]) {
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        if (p._prev == null || p.y == null) continue;
        moves.push({
          x: p.x,
          y0: p._prev,
          y1: p.y,
          color: p.y >= p._prev ? "green" : "red",
        });
      }
    }

    // RSI label texts
    const labelX = !isMobile
      ? [120, 120, 120, 120, 120, RSI_INTERVAL === "4h" ? 140 : null]
      : [100, 100, 100, 100, 100, RSI_INTERVAL === "4h" ? 100 : null];

    const labelY = [
      24,
      34,
      49,
      64,
      74,
      RSI_INTERVAL === "4h" && indicators ? indicators.rsi_average : null,
    ];

    const labelText = [
      "OVERSOLD",
      "WEAK",
      "NEUTRAL",
      "STRONG",
      "OVERBOUGHT",
      RSI_INTERVAL === "4h" && indicators && indicators.rsi_average != null
        ? `AVG RSI: ${Number(indicators.rsi_average).toFixed(2)}`
        : null,
    ];

    const rsiLabels = [];
    for (let i = 0; i < labelText.length; i++) {
      if (labelText[i] && labelX[i] != null && labelY[i] != null) {
        rsiLabels.push({ x: labelX[i], y: labelY[i], text: labelText[i] });
      }
    }

    // annotations
    const annotations = [
      { type: "box", yScaleID: "y-axis-1", yMin: 20, yMax: 30, backgroundColor: "rgba(0,102,0,0.2)", borderWidth: 0 },
      { type: "box", yScaleID: "y-axis-1", yMin: 30, yMax: 40, backgroundColor: "rgba(0,204,102,0.2)", borderWidth: 0 },
      { type: "box", yScaleID: "y-axis-1", yMin: 60, yMax: 70, backgroundColor: "rgba(255,153,153,0.2)", borderWidth: 0 },
      { type: "box", yScaleID: "y-axis-1", yMin: 70, yMax: 80, backgroundColor: "rgba(204,0,0,0.2)", borderWidth: 0 },

      { type: "line", mode: "horizontal", scaleID: "y-axis-1", value: 30, borderColor: "green", borderDash: [3, 4], borderWidth: 1.5 },
      { type: "line", mode: "horizontal", scaleID: "y-axis-1", value: 40, borderColor: "rgb(0,204,102)", borderDash: [3, 4], borderWidth: 1.5 },
      { type: "line", mode: "horizontal", scaleID: "y-axis-1", value: 60, borderColor: "rgb(255,153,153)", borderDash: [3, 4], borderWidth: 1.5 },
      { type: "line", mode: "horizontal", scaleID: "y-axis-1", value: 70, borderColor: "red", borderDash: [3, 4], borderWidth: 1.5 },
    ];

    if (RSI_INTERVAL === "4h" && indicators && indicators.rsi_average != null && isFinite(indicators.rsi_average)) {
      annotations.push({
        type: "line",
        mode: "horizontal",
        scaleID: "y-axis-1",
        value: indicators.rsi_average,
        borderColor: "orange",
        borderDash: [3, 4],
        borderWidth: 1.5,
      });
    }

    const chartEvents = isMobile
      ? ["touchstart", "click"]
      : ["mousemove", "mouseout", "click"];

    const config = {
      type: "scatter",
      data: {
        datasets: [
          {
            label: "RSI",
            data: points,
            pointRadius: (ctx) => ctx.dataset.data[ctx.dataIndex]._radius,
            pointHoverRadius: (ctx) => ctx.dataset.data[ctx.dataIndex]._radius,   // ✅ freeze
            pointHitRadius: (ctx) => ctx.dataset.data[ctx.dataIndex]._hitRadius,  // ✅ easy hover
            pointBackgroundColor: (ctx) => ctx.dataset.data[ctx.dataIndex]._color,
            pointBorderColor: () => pointBorderColor,
            pointBorderWidth: () => 1,
          },
        ],
      },
      options: {
        events: chartEvents,
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        legend: { display: false },
        zoom: {
          enabled: false,
          drag: false,
        },
        pan: {
          enabled: !isMobile,
          mode: "xy",
          speed: 20,
          threshold: 2,
        },

        _cw: {
          drawLabels: !!options["symbols"],
          drawMoves: !isMobile && !!options["moves"],
          moves,
          rsiLabels,
          darkmode,
          theme,
          report_time_download,
          titleLines: isMobile
            ? [
                { text: `RSI ${RSI_INTERVAL} Heatmap`, size: 13 },
                { text: `Updated: ${report_time}`, size: 9 },
              ]
            : [
                { text: `Crypto Market RSI(${RSI_INTERVAL}) Heatmap`, size: 16 },
                { text: `Updated: ${report_time} UTC by www.cryptowaves.app`, size: 12 },
              ],
          titleColor: theme.fontColor,
        },

        // 1) Tooltip ONLY when cursor is actually on a bubble:
        tooltips: {
          enabled: true,          // ✅ allow tooltips on mobile too
          mode: "nearest",
          intersect: true,
          callbacks: {
            label: (item, chartData) => {
              const p = chartData.datasets[item.datasetIndex].data[item.index];
              const coin = p._coin;

              const alertObj = alerts.find((a) => a.coin === coin.coin);
              const alertText = alertObj
                ? `${alertObj.signal} - ${dayjs(alertObj.timestamp).format("hh:mm a")}`
                : "none for 24 hours";

              return [
                `${coin.coin} - ${coin.name} (#${coin.rank})`,
                `RSI (4h): ${(coin.rsi != null ? Number(coin.rsi).toFixed(2) : "...")} | RSI (1D): ${(coin.rsi_1d != null ? Number(coin.rsi_1d).toFixed(2) : "...")}`,
                `Price: $${coin.current_price != null ? Number(coin.current_price).toFixed(4) : "..."}`,
                `1h, 24h, 7d, 30d: ${Number(coin.change_1h).toFixed(2)}%, ${Number(coin.change_24h).toFixed(2)}%, ${Number(coin.change_7d).toFixed(2)}%, ${Number(coin.change_30d).toFixed(2)}%`,
                `Latest Alert: ${alertText}`,
              ];
            },
          },
        },

        // 1) Cursor change when hovering a bubble
        hover: {
          mode: "nearest",
          intersect: true,
          onHover: function (e, activeElements) {
            if (!Charts.canvasEl) return;
            Charts.canvasEl.style.cursor = activeElements && activeElements.length ? "pointer" : "move";
          },
        },

        // 3) Pan / drag to move chart (mouse drag)
        // (Zoom disabled. Pan enabled.)
        pan: {
          enabled: !isMobile,
          mode: "xy",
          speed: 20,
          threshold: 2,
        },
        zoom: {
          enabled: false,
        },

        onClick: function (evt) {
          const chart = this.chart;
          const elems = chart.getElementsAtEventForMode(evt, "nearest", { intersect: true }, false);
          if (!elems || elems.length === 0) {
            PubSub.publish("HEATMAP_COIN_CARD_SELECTED", null);
            return;
          }

          const el = elems[0];
          const p = chart.data.datasets[el._datasetIndex].data[el._index];
          PubSub.publish("HEATMAP_COIN_CARD_SELECTED", p._coin);

          if (!isMobile) {
            PubSub.publish("COIN_SELECTED", p._coin.coin);
          }
        },

        title: { display: false },
        layout: {
          padding: {
            top: isMobile ? 100 : 100
          },
        },

        scales: {
          xAxes: [
            {
              type: "linear",
              scaleLabel: { display: true, labelString: "Coin Rank", fontColor: theme.fontColor },
              ticks: { min: 1, max: 155, fontColor: theme.fontColor },
              gridLines: { color: gridLineColor },
            },
          ],
          yAxes: [
            {
              type: "linear",
              scaleLabel: { display: !isMobile, labelString: `RSI (${RSI_INTERVAL})`, fontColor: theme.fontColor },
              ticks: { min: yMin, max: yMax, fontColor: theme.fontColor },
              gridLines: { color: gridLineColor },
            },
          ],
        },

        annotation: { annotations },
      },
    };

    if (!Charts.ctx2d) {
      throw new Error("Charts.rsi: call Charts.init(containerId) before Charts.rsi()");
    }

    // hide save button on mobile if you want
    /*if (Charts.saveBtn) {
      Charts.saveBtn.style.display = isMobile ? "none" : "block";
    }*/

    if (!Charts.chart) {
      Charts.chart = new Chart(Charts.ctx2d, config);
      Charts.firstDraw = 1;
    } else {
      Charts.chart.data = config.data;
      Charts.chart.options = config.options;
      Charts.chart.update(0);
    }
  },
};

export default Charts;
